import { useEffect, useMemo, useRef, useState } from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Eye,
  Infinity as InfinityIcon,
  Link2,
  Package,
  Plus,
  RotateCcw,
  Save,
  Trash2,
} from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

import {
  useCatalog,
  useCreateProposal,
  useDeleteProposal,
  useProposals,
  useUpdateProposal,
  type Proposal,
  type ProposalIn,
  type RenewalTerm,
} from "../api";
import {
  buildIndex,
  bundleLines,
  bundleMargin,
  bundlePrice,
  computeTotals,
  isFlatFee,
  lineMargin,
  lineSubtotal,
  priceOf,
  productName,
  qtyOf,
  unitName,
  type BundleRow,
  type DiscountRow,
  type LineRow,
} from "../calc";
import { BloodDripOverlay } from "@/components/cinch/BloodDrip";
import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { DatePicker, fromIsoDate, toIsoDate } from "@/components/cinch/DatePicker";
import { MarkdownNotes } from "@/components/cinch/MarkdownNotes/MarkdownNotes";
import { NumberInput } from "@/components/cinch/NumberInput";
import { useCan } from "@/lib/auth/useCan";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { CustomerPreview } from "../components/CustomerPreview";
import { ProposalTimelineChart } from "../components/ProposalTimelineChart";
import { SavedProposalsTable } from "../components/SavedProposalsTable";
import { discountStatus, displayValue } from "../discounts";
import {
  formatPrice,
  formatPrice2,
  marginAbsolutelyNot,
  marginAlertTitle,
  marginLabel,
  marginNote,
  marginRubric,
  marginVariant,
} from "../format";
import { buildProjection } from "../projection";
import { readPrefill } from "../handoff";
import { copyShareLink } from "../share";

const CATEGORY_LABELS: Record<string, string> = {
  subscription: "Subscription",
  usage: "Usage",
  pass_thru: "Pass-thru",
  services: "Services",
  other: "Other",
};

const DRAFT_KEY = "cinch-ops-proposal-draft-v1";
const DRAFT_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

const newLocalId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

interface Header {
  customer_name: string;
  proposal_name: string;
  contract_months: number;
  closer_id: string;
  locations: number | null;
  is_auto_renew: boolean;
  renewal_term: RenewalTerm;
  renewal_term_months: number | null;
  mtm_uplift_pct: number | null;
  billing_effective_from: string | null; // ISO YYYY-MM-DD
  notes: string;
}
const EMPTY_HEADER: Header = {
  customer_name: "",
  proposal_name: "",
  contract_months: 12,
  closer_id: "",
  locations: null,
  is_auto_renew: true,
  renewal_term: "SAME_TERM",
  renewal_term_months: null,
  mtm_uplift_pct: null,
  billing_effective_from: null,
  notes: "",
};

const isEmptyDraft = (
  h: Header,
  lines: LineRow[],
  discs: DiscountRow[],
  bundles: BundleRow[],
  id: string | null,
) =>
  !id &&
  lines.length === 0 &&
  discs.length === 0 &&
  bundles.length === 0 &&
  h.customer_name === "" &&
  h.proposal_name === "" &&
  h.contract_months === 12 &&
  h.closer_id === "" &&
  h.locations == null &&
  h.is_auto_renew &&
  h.renewal_term === "SAME_TERM" &&
  h.renewal_term_months == null &&
  h.mtm_uplift_pct == null &&
  h.billing_effective_from == null &&
  h.notes === "";

interface DraftShape {
  header: Header;
  lineItems: LineRow[];
  bundles: BundleRow[];
  discountRows: DiscountRow[];
  currentProposalId: string | null;
  /** False when the draft is a byte-identical copy of the proposal it was
   *  loaded from (loading persists too) — such a draft holds nothing unsaved,
   *  so a share link may overwrite it without asking. */
  dirty: boolean;
  /** Serialized state captured when a proposal was last loaded — the baseline
   *  `dirty` is computed against. Carried in the draft so a restore keeps it. */
  cleanSnapshot: string | null;
}

/** Read the persisted draft, or null when there is none worth restoring
 *  (absent, expired, unparseable, or empty). Saves clear the draft, so a
 *  non-null result always means unsaved work. */
const readDraft = (): DraftShape | null => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || typeof d.savedAt !== "number" || Date.now() - d.savedAt >= DRAFT_EXPIRY_MS) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    const header = { ...EMPTY_HEADER, ...(d.header || {}) };
    const lineItems = Array.isArray(d.lineItems) ? d.lineItems : [];
    const discountRows = Array.isArray(d.discountRows) ? d.discountRows : [];
    const bundles = Array.isArray(d.bundles) ? d.bundles : [];
    const currentProposalId = d.currentProposalId ?? null;
    if (isEmptyDraft(header, lineItems, discountRows, bundles, currentProposalId)) return null;
    return {
      header,
      lineItems,
      bundles,
      discountRows,
      currentProposalId,
      // Drafts written before dirty-tracking existed count as dirty — the safe
      // reading, since we can no longer tell.
      dirty: d.dirty !== false,
      cleanSnapshot: typeof d.cleanSnapshot === "string" ? d.cleanSnapshot : null,
    };
  } catch {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
};

export function ProposalsPage() {
  const canWrite = useCan("proposals.write");
  const navigate = useNavigate();
  const search = useSearch({ from: "/proposals" });
  const catalogQ = useCatalog();
  const proposalsQ = useProposals();
  const createM = useCreateProposal();
  const updateM = useUpdateProposal();
  const deleteM = useDeleteProposal();

  const catalog = catalogQ.data;
  const proposals = proposalsQ.data ?? [];

  const [header, setHeader] = useState<Header>(EMPTY_HEADER);
  const [lineItems, setLineItems] = useState<LineRow[]>([]);
  const [bundles, setBundles] = useState<BundleRow[]>([]);
  const [discountRows, setDiscountRows] = useState<DiscountRow[]>([]);
  const [currentProposalId, setCurrentProposalId] = useState<string | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveCustomer, setSaveCustomer] = useState("");
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [marginInfoOpen, setMarginInfoOpen] = useState(false);
  const [bleeding, setBleeding] = useState(false);
  const pendingSaveRef = useRef<(() => void) | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [tab, setTab] = useState<"calculator" | "saved">("calculator");
  const skipPersist = useRef(false);
  /** A share link's proposal id, waiting for the proposals list to arrive. */
  const [pendingLinkId, setPendingLinkId] = useState<string | null>(null);
  /** Share link met an unsaved draft — the user picks which one wins. While
   *  this is set, `draftLoaded` stays false so the persist effect can't wipe
   *  the stored draft out from under the dialog. */
  const [linkConflict, setLinkConflict] = useState<{ id: string; draft: DraftShape } | null>(
    null,
  );

  /** Baseline for dirty-tracking: the serialized state as of the last
   *  loadProposal. Null when the calculator never loaded a saved proposal. */
  const cleanSnapshotRef = useRef<string | null>(null);
  /** Set by loadProposal; the next persist run adopts its state as clean. */
  const markCleanRef = useRef(false);

  const restoreDraft = (d: DraftShape) => {
    cleanSnapshotRef.current = d.cleanSnapshot;
    setHeader(d.header);
    setLineItems(d.lineItems);
    setBundles(d.bundles);
    setDiscountRows(d.discountRows);
    setCurrentProposalId(d.currentProposalId);
  };

  // ── Draft cache + share link: hydrate once ─────────────────────────────────
  useEffect(() => {
    const linkedId = search.proposal ?? null;
    // A one-shot prefill (e.g. "Create Proposal" from a contract) wins over the
    // persisted draft — the user explicitly asked to build from that source.
    const prefill = readPrefill();
    if (prefill) {
      setHeader({ ...EMPTY_HEADER, ...prefill.header });
      setLineItems(prefill.lineItems);
      setBundles(prefill.bundles ?? []);
      setDiscountRows(prefill.discountRows ?? []);
      setCurrentProposalId(null);
      setTab("calculator");
      toast.info("Prefilled from contract — review and save.");
      if (linkedId) void navigate({ to: "/proposals", search: {}, replace: true });
      setDraftLoaded(true);
      return;
    }
    const draft = readDraft();
    if (linkedId) {
      if (draft?.dirty) {
        // Arriving on a share link with unsaved work cached: don't clobber it —
        // the dialog lets the user review the draft before the link loads.
        setLinkConflict({ id: linkedId, draft });
        return; // draftLoaded stays false until the dialog resolves.
      }
      // No draft, or a clean copy of an already-saved proposal — nothing
      // unsaved to protect, so the link loads straight away.
      setPendingLinkId(linkedId);
    } else if (draft) {
      restoreDraft(draft);
      toast.info("Draft restored — picked up where you left off.");
    }
    setDraftLoaded(true);
    // Mount-only by design: a share link is honored on arrival, not watched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reviewDraftInstead = () => {
    if (!linkConflict) return;
    restoreDraft(linkConflict.draft);
    setLinkConflict(null);
    setDraftLoaded(true);
    void navigate({ to: "/proposals", search: {}, replace: true });
    toast.info("Draft restored — the shared proposal was not loaded.");
  };

  const loadSharedInstead = () => {
    if (!linkConflict) return;
    setPendingLinkId(linkConflict.id);
    setLinkConflict(null);
    setDraftLoaded(true);
  };

  // ── Draft cache: persist on change ─────────────────────────────────────────
  useEffect(() => {
    if (!draftLoaded) return;
    if (skipPersist.current) {
      skipPersist.current = false;
      return;
    }
    try {
      if (isEmptyDraft(header, lineItems, discountRows, bundles, currentProposalId)) {
        localStorage.removeItem(DRAFT_KEY);
        return;
      }
      const snapshot = JSON.stringify({ header, lineItems, bundles, discountRows, currentProposalId });
      if (markCleanRef.current) {
        // This persist is the loadProposal write itself — adopt it as the
        // clean baseline, so an untouched loaded proposal never reads dirty.
        markCleanRef.current = false;
        cleanSnapshotRef.current = snapshot;
      }
      localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({
          header,
          lineItems,
          bundles,
          discountRows,
          currentProposalId,
          dirty: snapshot !== cleanSnapshotRef.current,
          cleanSnapshot: cleanSnapshotRef.current,
          savedAt: Date.now(),
        }),
      );
    } catch {
      /* localStorage full/unavailable — skip */
    }
  }, [header, lineItems, bundles, discountRows, currentProposalId, draftLoaded]);

  const clearDraft = () => {
    skipPersist.current = true;
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {
      /* ignore */
    }
  };

  const idx = useMemo(
    () =>
      buildIndex(
        catalog ?? { items: [], packages: [], discounts: [], price_metrics: [], closers: [] },
      ),
    [catalog],
  );

  const productOptions: ComboOption[] = useMemo(() => {
    if (!catalog) return [];
    const items = catalog.items
      .filter((i) => !i.is_archived)
      .map((i) => ({
        id: `item:${i.id}`,
        label: i.name,
        subtitle: CATEGORY_LABELS[i.category ?? ""] ?? "Item",
        group: "Items",
        keywords: i.sku_cinch ?? "",
      }));
    const pkgs = catalog.packages
      .filter((p) => p.proposal_eligible && !p.is_archived)
      .map((p) => ({
        id: `pkg:${p.id}`,
        label: p.name,
        subtitle: "Package",
        group: "Packages",
        badge: p.pricing_type === "flat_fee" ? "Flat" : "Per Unit",
        keywords: p.sku ?? "",
      }));
    return [...items, ...pkgs];
  }, [catalog]);

  const totals = useMemo(
    () => computeTotals(idx, lineItems, discountRows, header.contract_months, bundles),
    [idx, lineItems, discountRows, header.contract_months, bundles],
  );

  // Renewal config drives both the summary's 1st-12mo value and the timeline.
  const renewal = useMemo(
    () => ({
      termMonths: header.contract_months,
      isAutoRenew: header.is_auto_renew,
      renewalTerm: header.renewal_term,
      renewalTermMonths: header.renewal_term_months,
      mtmUpliftPct: header.mtm_uplift_pct,
      billingEffectiveFrom: header.billing_effective_from,
    }),
    [header],
  );
  const projection = useMemo(
    () => buildProjection(idx, lineItems, discountRows, renewal, bundles),
    [idx, lineItems, discountRows, renewal, bundles],
  );

  const validLines = lineItems.filter((l) => l.item_id || l.package_id);
  const closers = catalog?.closers ?? [];
  const editingName = proposals.find((p) => p.id === currentProposalId)?.name;

  // ── Line item mutations ────────────────────────────────────────────────────
  const addLine = () =>
    setLineItems((prev) => [...prev, { _id: newLocalId(), quantity: 1, unit_price: 0 }]);

  const removeLine = (id: string) => {
    setLineItems((prev) => prev.filter((li) => li._id !== id));
    setDiscountRows((prev) =>
      prev.map((r) => (r.target_line_local_id === id ? { ...r, target_line_local_id: null } : r)),
    );
  };

  const updateLine = (id: string, patch: Partial<LineRow>) =>
    setLineItems((prev) => prev.map((li) => (li._id === id ? { ...li, ...patch } : li)));

  const selectProduct = (id: string, optionId: string | null) => {
    if (!optionId) {
      updateLine(id, { item_id: undefined, package_id: undefined });
      return;
    }
    const sep = optionId.indexOf(":");
    const kind = optionId.slice(0, sep);
    const refId = optionId.slice(sep + 1);
    if (kind === "item") {
      const item = idx.itemById.get(refId);
      updateLine(id, {
        item_id: refId,
        package_id: undefined,
        unit_price: item?.recommended_price ?? 0,
        quantity: 1,
      });
    } else {
      const pkg = idx.pkgById.get(refId);
      updateLine(id, {
        package_id: refId,
        item_id: undefined,
        unit_price: pkg?.price ?? 0,
        quantity: 1,
      });
    }
  };

  const quickFill = (id: string, kind: "floor" | "starting" | "recommended") => {
    const li = lineItems.find((l) => l._id === id);
    const item = li?.item_id ? idx.itemById.get(li.item_id) : undefined;
    if (!item) return;
    const price =
      kind === "floor" ? item.floor_price : kind === "starting" ? item.starting_price : item.recommended_price;
    updateLine(id, { unit_price: price });
  };

  const lineProductValue = (li: LineRow): string | null =>
    li.item_id ? `item:${li.item_id}` : li.package_id ? `pkg:${li.package_id}` : null;

  // ── Bundle ("package") mutations ───────────────────────────────────────────
  const addBundle = () =>
    setBundles((prev) => [
      ...prev,
      {
        _id: newLocalId(),
        name: "",
        sku: null,
        descr: null,
        customer_facing_descr: null,
        pricing_type: "flat_fee",
        price: null,
        price_metric: null,
      },
    ]);

  const updateBundle = (id: string, patch: Partial<BundleRow>) =>
    setBundles((prev) => prev.map((b) => (b._id === id ? { ...b, ...patch } : b)));

  // Removing a package unassigns its member lines (they become loose again).
  const removeBundle = (id: string) => {
    setBundles((prev) => prev.filter((b) => b._id !== id));
    setLineItems((prev) =>
      prev.map((li) => (li.bundle_local_id === id ? { ...li, bundle_local_id: null } : li)),
    );
  };

  // ── Discount mutations ─────────────────────────────────────────────────────
  const addDiscountRow = () =>
    setDiscountRows((prev) => [
      ...prev,
      { _id: newLocalId(), discount_id: "", target_line_local_id: null, duration_override: null, unit_overrides: {} },
    ]);

  const updateDiscountRow = (id: string, patch: Partial<DiscountRow>) =>
    setDiscountRows((prev) => prev.map((r) => (r._id === id ? { ...r, ...patch } : r)));

  const removeDiscountRow = (id: string) =>
    setDiscountRows((prev) => prev.filter((r) => r._id !== id));

  const updateUnitOverride = (rowId: string, metricId: string, qty: number | null) =>
    setDiscountRows((prev) =>
      prev.map((r) => {
        if (r._id !== rowId) return r;
        const next = { ...r.unit_overrides };
        if (qty == null) delete next[metricId];
        else next[metricId] = qty;
        return { ...r, unit_overrides: next };
      }),
    );

  const discountOptions = (forRowId: string): ComboOption[] => {
    if (!catalog) return [];
    return catalog.discounts
      .filter((d) => {
        if (!discountStatus(d).active) return false;
        if (d.stackable) return true;
        const usedElsewhere = discountRows.some((r) => r._id !== forRowId && r.discount_id === d.id);
        return !usedElsewhere;
      })
      .map((d) => ({
        id: d.id,
        label: d.name,
        subtitle: `${displayValue(d, catalog.price_metrics)}${d.stackable ? " · stackable" : ""}`,
      }));
  };

  // ── Reset / save / load ────────────────────────────────────────────────────
  const resetCalc = () => {
    setHeader(EMPTY_HEADER);
    setLineItems([]);
    setBundles([]);
    setDiscountRows([]);
    setCurrentProposalId(null);
    void navigate({ to: "/proposals", search: {}, replace: true });
  };

  const buildPayload = (nameOverride?: string, customerOverride?: string): ProposalIn => ({
    name: (nameOverride ?? header.proposal_name ?? "").trim(),
    customer_name: (customerOverride ?? header.customer_name ?? "").trim(),
    contract_months: header.contract_months || 12,
    closer_id: header.closer_id || null,
    closer_name: header.closer_id ? (closers.find((c) => c.id === header.closer_id)?.name ?? null) : null,
    locations: header.locations,
    is_auto_renew: header.is_auto_renew,
    renewal_term: header.renewal_term,
    renewal_term_months:
      header.renewal_term === "DIFFERENT_TERM" ? (header.renewal_term_months ?? null) : null,
    mtm_uplift_pct:
      header.renewal_term === "MONTH_TO_MONTH" ? (header.mtm_uplift_pct ?? null) : null,
    billing_effective_from: header.billing_effective_from,
    notes: header.notes.trim() || null,
    bundles: bundles.map((b) => ({
      local_id: b._id,
      name: b.name.trim(),
      sku: b.sku?.trim() || null,
      descr: b.descr?.trim() || null,
      customer_facing_descr: b.customer_facing_descr?.trim() || null,
      pricing_type: b.pricing_type || "flat_fee",
      price: b.price ?? null,
      price_metric: b.price_metric || null,
    })),
    line_items: validLines.map((li) => ({
      local_id: li._id,
      bundle_local_id: li.bundle_local_id ?? null,
      item_id: li.item_id ?? null,
      package_id: li.package_id ?? null,
      quantity: qtyOf(li) || 1,
      display_as_unlimited: !!li.display_as_unlimited,
      unit_price: priceOf(li),
      subtotal: lineSubtotal(li),
      gross_margin: lineMargin(idx, li),
    })),
    discounts: discountRows
      .filter((r) => r.discount_id)
      .map((r) => ({
        discount_id: r.discount_id,
        target_line_local_id: r.target_line_local_id || null,
        duration_override: r.duration_override ?? null,
        unit_overrides: Object.entries(r.unit_overrides || {})
          .filter(([, q]) => q != null)
          .map(([price_metric_id, q]) => ({ price_metric_id, quantity: Number(q) })),
      })),
  });

  const openSaveDialog = () => {
    setSaveName(header.proposal_name || "");
    setSaveCustomer(header.customer_name || "");
    setSaveOpen(true);
  };

  const handleSave = async () => {
    if (!saveName.trim()) return;
    try {
      const created = await createM.mutateAsync(buildPayload(saveName, saveCustomer));
      setCurrentProposalId(created.id);
      setHeader((h) => ({ ...h, proposal_name: saveName.trim(), customer_name: saveCustomer.trim() }));
      setSaveOpen(false);
      clearDraft();
      void navigate({ to: "/proposals", search: { proposal: created.id }, replace: true });
      toast.success("Proposal saved.");
    } catch {
      toast.error("Couldn't save the proposal.");
    }
  };

  const handleReplace = async () => {
    if (!currentProposalId) return;
    try {
      await updateM.mutateAsync({ id: currentProposalId, body: buildPayload() });
      setReplaceOpen(false);
      clearDraft();
      toast.success("Proposal updated.");
    } catch {
      toast.error("Couldn't update the proposal.");
    }
  };

  /** Saving an "Absolutely Not" margin (<30%) still works — but the screen
   *  bleeds for ~5s first. The save runs from the overlay's onComplete. */
  const withBloodPenalty = (save: () => void) => {
    if (marginAbsolutelyNot(totals.blendedMargin) && validLines.length > 0 && !bleeding) {
      pendingSaveRef.current = save;
      setBleeding(true);
    } else {
      save();
    }
  };

  const loadProposal = (p: Proposal) => {
    markCleanRef.current = true; // the persist this triggers becomes the clean baseline
    setHeader({
      customer_name: p.customer_name,
      proposal_name: p.name,
      contract_months: p.contract_months,
      closer_id: p.closer_id ?? "",
      locations: p.locations ?? null,
      is_auto_renew: p.is_auto_renew ?? true,
      renewal_term: p.renewal_term ?? "SAME_TERM",
      renewal_term_months: p.renewal_term_months ?? null,
      mtm_uplift_pct: p.mtm_uplift_pct ?? null,
      billing_effective_from: p.billing_effective_from ?? null,
      notes: p.notes ?? "",
    });
    // Bundles first, so lines can map their server bundle_id → the new local id.
    const bundleServerToLocal: Record<string, string> = {};
    setBundles(
      (p.bundles || []).map((b) => {
        const local = newLocalId();
        bundleServerToLocal[b.id] = local;
        return {
          _id: local,
          name: b.name,
          sku: b.sku,
          descr: b.descr,
          customer_facing_descr: b.customer_facing_descr,
          pricing_type: b.pricing_type,
          price: b.price,
          price_metric: b.price_metric,
        };
      }),
    );
    const serverToLocal: Record<string, string> = {};
    setLineItems(
      (p.line_items || []).map((li) => {
        const local = newLocalId();
        serverToLocal[li.id] = local;
        return {
          _id: local,
          bundle_local_id: li.bundle_id ? bundleServerToLocal[li.bundle_id] ?? null : null,
          item_id: li.item_id ?? undefined,
          package_id: li.package_id ?? undefined,
          quantity: li.quantity,
          display_as_unlimited: li.display_as_unlimited,
          unit_price: li.unit_price,
        };
      }),
    );
    setDiscountRows(
      (p.discounts || []).map((d) => {
        const overrides: Record<string, number> = {};
        (d.unit_overrides || []).forEach((u) => {
          overrides[u.price_metric_id] = u.quantity;
        });
        return {
          _id: newLocalId(),
          discount_id: d.discount_id,
          target_line_local_id: d.target_line_item_id ? serverToLocal[d.target_line_item_id] ?? null : null,
          duration_override: d.duration_override ?? null,
          unit_overrides: overrides,
        };
      }),
    );
    setCurrentProposalId(p.id);
    setTab("calculator"); // Load jumps back to the builder showing this proposal.
    // Keep the address bar shareable: the URL always points at the open proposal.
    void navigate({ to: "/proposals", search: { proposal: p.id }, replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Share link: load once the proposals list is here ───────────────────────
  useEffect(() => {
    if (!pendingLinkId || proposalsQ.isLoading) return;
    if (proposalsQ.isError) {
      toast.error("Couldn't open the shared proposal — the proposals list failed to load.");
      setPendingLinkId(null);
      return;
    }
    const p = (proposalsQ.data ?? []).find((x) => x.id === pendingLinkId);
    if (p) {
      loadProposal(p);
      toast.info(`Opened shared proposal "${p.name}".`);
    } else {
      toast.error("That shared proposal no longer exists — it may have been deleted.");
      void navigate({ to: "/proposals", search: {}, replace: true });
    }
    setPendingLinkId(null);
    // loadProposal/navigate are stable enough for this one-shot consume.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingLinkId, proposalsQ.data, proposalsQ.isLoading, proposalsQ.isError]);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this proposal?")) return;
    try {
      await deleteM.mutateAsync(id);
      if (id === currentProposalId) {
        setCurrentProposalId(null);
        void navigate({ to: "/proposals", search: {}, replace: true });
      }
      toast.success("Proposal deleted.");
    } catch {
      toast.error("Couldn't delete the proposal.");
    }
  };

  if (catalogQ.isLoading) {
    return (
      <div className="space-y-4 p-1">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const catalogDegraded = !!catalog && catalog.packages.length === 0 && catalog.discounts.length === 0;
  const attention = totals.blendedMargin < 65 && validLines.length > 0;

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6">
      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Proposals</h1>
          <p className="text-sm text-muted-foreground">Build pricing proposals from the Cinch catalog.</p>
        </div>
        {tab === "calculator" && (
          <div className="flex flex-wrap items-center gap-2">
            {currentProposalId && (
              <Badge variant="info" className="mr-1">
                Editing: {editingName ?? "—"}
              </Badge>
            )}
            {currentProposalId && (
              <Button variant="outline" onClick={() => void copyShareLink(currentProposalId)}>
                <Link2 className="mr-1 h-4 w-4" />
                Copy Link
              </Button>
            )}
            <Button variant="outline" onClick={resetCalc}>
              <RotateCcw className="mr-1 h-4 w-4" />
              Clear
            </Button>
            {canWrite && currentProposalId && (
              <Button variant="outline" disabled={validLines.length === 0} onClick={openSaveDialog}>
                <Save className="mr-1 h-4 w-4" />
                Save As New
              </Button>
            )}
            {canWrite && (
              <Button
                disabled={validLines.length === 0}
                onClick={currentProposalId ? () => setReplaceOpen(true) : openSaveDialog}
              >
                <Save className="mr-1 h-4 w-4" />
                {currentProposalId ? "Save" : "Save Proposal"}
              </Button>
            )}
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as "calculator" | "saved")}>
        <TabsList variant="underline">
          <TabsTrigger value="calculator">Calculator</TabsTrigger>
          <TabsTrigger value="saved" count={proposals.length}>
            Saved Proposals
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calculator" className="space-y-6">

      {catalogQ.isError &&
        ((catalogQ.error as ApiError)?.status === 403 ? (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Your account can't load the proposals catalog</AlertTitle>
            <AlertDescription>
              Building proposals reads the Cinch product catalog, which requires Cinch
              billing/superuser access — your account doesn't have it. You can still view saved
              proposals below. Ask an admin if you need to build proposals.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="danger">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Couldn't load the Cinch catalog</AlertTitle>
            <AlertDescription>
              Items, packages, and discounts couldn't be fetched from the Cinch API. You can still
              load and view saved proposals; product selection is unavailable until the catalog
              loads.
            </AlertDescription>
          </Alert>
        ))}

      {catalogDegraded && (
        <Alert variant="info">
          <AlertTitle>Catalog partially available</AlertTitle>
          <AlertDescription>
            Packages and discounts aren't being returned by the Cinch API on this environment yet —
            you can still build proposals from items. They'll appear automatically once that work
            lands.
          </AlertDescription>
        </Alert>
      )}

      {/* Header inputs */}
      <Card>
        <CardContent className="grid grid-cols-1 gap-4 pt-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="customer">Customer Name</Label>
            <Input
              id="customer"
              placeholder="Acme Corp"
              value={header.customer_name}
              onChange={(e) => setHeader({ ...header, customer_name: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pname">Proposal Name</Label>
            <Input
              id="pname"
              placeholder="Q3 2026 Proposal"
              value={header.proposal_name}
              onChange={(e) => setHeader({ ...header, proposal_name: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="months">Contract Length (months)</Label>
            <Input
              id="months"
              type="number"
              min={1}
              value={header.contract_months}
              onChange={(e) => setHeader({ ...header, contract_months: parseInt(e.target.value) || 12 })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Closer</Label>
            <Select
              value={header.closer_id || "__none__"}
              onValueChange={(v) => setHeader({ ...header, closer_id: v === "__none__" ? "" : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select closer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Select closer —</SelectItem>
                {closers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="locations"># of Locations</Label>
            <NumberInput
              id="locations"
              maxDecimals={0}
              placeholder="e.g. 12"
              value={header.locations}
              onChange={(v) => setHeader({ ...header, locations: v })}
            />
          </div>

          {/* Renewal lifecycle — drives the timeline's term/renewal boundaries. */}
          <div className="flex flex-wrap items-end gap-4 border-t pt-4 sm:col-span-2 lg:col-span-4">
            <div className="flex items-center gap-2 pb-2">
              <Switch
                id="autorenew"
                checked={header.is_auto_renew}
                onCheckedChange={(v) => setHeader({ ...header, is_auto_renew: v })}
              />
              <Label htmlFor="autorenew">Auto-renews (evergreen)</Label>
            </div>
            {header.is_auto_renew && (
              <div className="space-y-1.5">
                <Label>Renews Into</Label>
                <Select
                  value={header.renewal_term}
                  onValueChange={(v) => setHeader({ ...header, renewal_term: v as RenewalTerm })}
                >
                  <SelectTrigger className="w-56">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SAME_TERM">Same term</SelectItem>
                    <SelectItem value="DIFFERENT_TERM">Different term</SelectItem>
                    <SelectItem value="MONTH_TO_MONTH">Month-to-month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {header.is_auto_renew && header.renewal_term === "DIFFERENT_TERM" && (
              <div className="space-y-1.5">
                <Label>Renewal Term (months)</Label>
                <NumberInput
                  className="w-40"
                  maxDecimals={0}
                  placeholder="e.g. 12"
                  value={header.renewal_term_months}
                  onChange={(v) => setHeader({ ...header, renewal_term_months: v })}
                />
              </div>
            )}
            {header.is_auto_renew && header.renewal_term === "MONTH_TO_MONTH" && (
              <div className="space-y-1.5">
                <Label>Month-to-Month Uplift (%)</Label>
                <NumberInput
                  className="w-40"
                  maxDecimals={2}
                  placeholder="e.g. 10"
                  value={header.mtm_uplift_pct}
                  onChange={(v) => setHeader({ ...header, mtm_uplift_pct: v })}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Billing Effective</Label>
              <DatePicker
                aria-label="Billing effective date"
                placeholder="Pick a date"
                value={fromIsoDate(header.billing_effective_from)}
                onChange={(d) => setHeader({ ...header, billing_effective_from: d ? toIsoDate(d) : null })}
              />
            </div>
            <div className="ml-auto flex items-end pb-0.5">
              <Button
                variant="outline"
                disabled={validLines.length === 0}
                onClick={() => setPreviewOpen(true)}
                title="Preview what the customer sees on the quote"
              >
                <Eye className="mr-1 h-4 w-4" />
                Customer Preview
              </Button>
            </div>
          </div>

          {!header.is_auto_renew && (
            <div className="sm:col-span-2 lg:col-span-4">
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Non-renewing term — requires explicit approval</AlertTitle>
                <AlertDescription>
                  A fixed-term (non-evergreen) deal expires at term end (bills $0 after{" "}
                  {header.contract_months} months) and <strong>requires explicit approval</strong>. It
                  won't be considered for T4 or T5 customers, and most likely not for T3.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Line items */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Line Items</CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={addBundle}>
              <Package className="mr-1 h-4 w-4" />
              Add Custom Package
            </Button>
            <Button size="sm" onClick={addLine}>
              <Plus className="mr-1 h-4 w-4" />
              Add Line Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {lineItems.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              <p>No line items yet.</p>
              <Button className="mt-3" onClick={addLine}>
                <Plus className="mr-1 h-4 w-4" />
                Add your first line item
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[1080px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-56">Product</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead className="w-36">Qty</TableHead>
                    <TableHead align="right">Floor</TableHead>
                    <TableHead align="right">Starting</TableHead>
                    <TableHead align="right">Rec.</TableHead>
                    <TableHead className="min-w-72">Unit Price</TableHead>
                    <TableHead align="right">Subtotal / mo</TableHead>
                    <TableHead>Margin</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.map((li) => {
                    const item = li.item_id ? idx.itemById.get(li.item_id) : undefined;
                    const flat = isFlatFee(idx, li);
                    const margin = lineMargin(idx, li);
                    const hasProduct = !!(li.item_id || li.package_id);
                    return (
                      <TableRow key={li._id}>
                        <TableCell>
                          <Combobox
                            options={productOptions}
                            value={lineProductValue(li)}
                            onChange={(v) => selectProduct(li._id, v)}
                            placeholder="Select product…"
                            searchPlaceholder="Search items & packages…"
                          />
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {unitName(idx, li)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <NumberInput
                              className="w-24"
                              align="right"
                              maxDecimals={0}
                              disabled={flat}
                              value={flat ? 1 : li.quantity}
                              onChange={(v) => {
                                if (flat) return;
                                updateLine(li._id, { quantity: v });
                              }}
                            />
                            {li.item_id && !flat && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <label className="flex cursor-pointer items-center gap-1 text-muted-foreground transition-colors hover:text-foreground data-[state=delayed-open]:text-foreground">
                                    <Checkbox
                                      checked={!!li.display_as_unlimited}
                                      onCheckedChange={(v) =>
                                        updateLine(li._id, { display_as_unlimited: v === true })
                                      }
                                    />
                                    <InfinityIcon className="h-4 w-4" />
                                  </label>
                                </TooltipTrigger>
                                <TooltipContent tone="info">Customer display as unlimited</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                        <TableCell align="right" className="whitespace-nowrap text-sm text-muted-foreground">
                          {item ? formatPrice(item.floor_price) : "—"}
                        </TableCell>
                        <TableCell align="right" className="whitespace-nowrap text-sm text-muted-foreground">
                          {item ? formatPrice(item.starting_price) : "—"}
                        </TableCell>
                        <TableCell align="right" className="whitespace-nowrap text-sm text-muted-foreground">
                          {item ? formatPrice(item.recommended_price) : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <NumberInput
                              className="w-40"
                              prefix="$"
                              maxDecimals={8}
                              value={li.unit_price}
                              onChange={(v) => updateLine(li._id, { unit_price: v })}
                            />
                            {item && (
                              <div className="flex">
                                {(["floor", "starting", "recommended"] as const).map((k, i) => (
                                  <Button
                                    key={k}
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className={
                                      i === 0
                                        ? "rounded-r-none px-2"
                                        : i === 2
                                          ? "rounded-l-none border-l-0 px-2"
                                          : "rounded-none border-l-0 px-2"
                                    }
                                    title={`${k}: ${formatPrice(
                                      k === "floor"
                                        ? item.floor_price
                                        : k === "starting"
                                          ? item.starting_price
                                          : item.recommended_price,
                                    )}`}
                                    onClick={() => quickFill(li._id, k)}
                                  >
                                    {k.charAt(0).toUpperCase()}
                                  </Button>
                                ))}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell align="right" className="whitespace-nowrap font-medium">
                          {formatPrice(lineSubtotal(li))}
                        </TableCell>
                        <TableCell>
                          {hasProduct && priceOf(li) > 0 ? (
                            <Badge variant={marginVariant(margin)}>{margin.toFixed(1)}%</Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeLine(li._id)}
                            aria-label="Remove line"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Custom packages — only shown once one is added (via "Add Custom Package"). */}
      {bundles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Custom Packages</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Present a group of line items as one line to the customer (a one-off package for this
              deal). The customer sees a single price; margin is still tracked from the underlying
              items. For products you sell often, build a package in Cinch billing and add it as a
              line item instead.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {bundles.map((b) => {
              const members = bundleLines(b, lineItems);
              const autoSum = members.reduce((s, li) => s + lineSubtotal(li), 0);
              const effective = bundlePrice(b, lineItems);
              const margin = bundleMargin(idx, b, lineItems);
              const assignable = lineItems.filter((l) => l.item_id);
              return (
                <div key={b._id} className="rounded-md border p-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="space-y-1.5 lg:col-span-2">
                      <Label>Package Name</Label>
                      <Input
                        placeholder="e.g. Growth Bundle"
                        value={b.name}
                        onChange={(e) => updateBundle(b._id, { name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Pricing Type</Label>
                      <Select
                        value={b.pricing_type}
                        onValueChange={(v) => updateBundle(b._id, { pricing_type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="flat_fee">Flat Fee</SelectItem>
                          <SelectItem value="per_unit">Per Unit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Price Override</Label>
                      <NumberInput
                        prefix="$"
                        maxDecimals={2}
                        placeholder={`Auto ${formatPrice2(autoSum)}`}
                        value={b.price}
                        onChange={(v) => updateBundle(b._id, { price: v })}
                      />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2 lg:col-span-4">
                      <Label>Customer-facing Description</Label>
                      <Input
                        placeholder="What the customer sees under the package name (optional)"
                        value={b.customer_facing_descr ?? ""}
                        onChange={(e) =>
                          updateBundle(b._id, { customer_facing_descr: e.target.value || null })
                        }
                      />
                    </div>
                  </div>

                  {/* Items included — assign line items to this package. */}
                  <div className="mt-3 border-t pt-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Items included
                    </p>
                    {assignable.length === 0 ? (
                      <p className="mt-1.5 text-sm text-muted-foreground">
                        Add product line items above, then check the ones that belong in this
                        package.
                      </p>
                    ) : (
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {assignable.map((li) => {
                          const inThis = li.bundle_local_id === b._id;
                          const inOther = !!li.bundle_local_id && !inThis;
                          return (
                            <label key={li._id} className="flex items-center gap-2 text-sm">
                              <Checkbox
                                checked={inThis}
                                onCheckedChange={(v) =>
                                  updateLine(li._id, { bundle_local_id: v === true ? b._id : null })
                                }
                              />
                              <span className={inOther ? "text-muted-foreground" : ""}>
                                {productName(idx, li)}
                                {inOther && <span className="text-xs"> (in another package)</span>}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                    <p className="text-sm text-muted-foreground">
                      {members.length} item{members.length === 1 ? "" : "s"} · Auto-sum{" "}
                      {formatPrice2(autoSum)}
                      {b.price != null && <> · Customer price {formatPrice2(effective)}</>}
                    </p>
                    <div className="flex items-center gap-2">
                      {members.length > 0 && effective > 0 && (
                        <Badge variant={marginVariant(margin)}>{margin.toFixed(1)}% margin</Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeBundle(b._id)}
                        aria-label="Remove package"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Discounts */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Discounts</CardTitle>
          <Button
            size="sm"
            variant={catalog?.discounts.length ? "default" : "outline"}
            onClick={addDiscountRow}
            disabled={!catalog?.discounts.length}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add Discount
          </Button>
        </CardHeader>
        <CardContent>
          {discountRows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {catalog?.discounts.length
                ? "No discounts yet. Pick from saved discounts to apply one."
                : "No discounts available from the Cinch catalog yet."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-64">Discount</TableHead>
                    <TableHead className="min-w-48">Target</TableHead>
                    <TableHead className="min-w-56">Overrides</TableHead>
                    <TableHead align="right">Reduction / mo</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {discountRows.map((row) => {
                    const d = catalog?.discounts.find((x) => x.id === row.discount_id) ?? null;
                    const summary = totals.reductions[row._id] ?? { reduction: 0, warning: "" };
                    return (
                      <TableRow key={row._id}>
                        <TableCell>
                          <Combobox
                            options={discountOptions(row._id)}
                            value={row.discount_id || null}
                            onChange={(v) =>
                              updateDiscountRow(row._id, {
                                discount_id: v || "",
                                duration_override: null,
                                unit_overrides: {},
                              })
                            }
                            placeholder="Select discount…"
                            searchPlaceholder="Search discounts…"
                          />
                          {d && (
                            <p className="mt-1 text-xs text-muted-foreground">
                              {displayValue(d, catalog?.price_metrics ?? [])}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={row.target_line_local_id ?? "__all__"}
                            onValueChange={(v) =>
                              updateDiscountRow(row._id, {
                                target_line_local_id: v === "__all__" ? null : v,
                              })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">Whole proposal</SelectItem>
                              {validLines.map((li) => (
                                <SelectItem key={li._id} value={li._id}>
                                  {productName(idx, li)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {!d && <span className="text-sm text-muted-foreground">—</span>}
                          {d?.type === "time_based" && (
                            <NumberInput
                              maxDecimals={0}
                              placeholder={String(d.duration_periods ?? "")}
                              value={row.duration_override}
                              onChange={(v) => updateDiscountRow(row._id, { duration_override: v })}
                            />
                          )}
                          {d?.type === "quantity_based" && (
                            <div className="space-y-1.5">
                              {(d.unit_grants || []).map((g) => {
                                const mName = idx.metricNameById.get(g.price_metric ?? "") || "unit";
                                return (
                                  <NumberInput
                                    key={g.price_metric ?? mName}
                                    align="right"
                                    placeholder={`${mName}: ${g.quantity}`}
                                    value={row.unit_overrides[g.price_metric ?? ""] ?? null}
                                    onChange={(v) =>
                                      updateUnitOverride(row._id, g.price_metric ?? "", v)
                                    }
                                  />
                                );
                              })}
                            </div>
                          )}
                          {d && d.type !== "time_based" && d.type !== "quantity_based" && (
                            <span className="text-sm text-muted-foreground">No overrides</span>
                          )}
                          {summary.warning && (
                            <p className="mt-1 text-xs text-warning">{summary.warning}</p>
                          )}
                        </TableCell>
                        <TableCell align="right" className="whitespace-nowrap font-medium">
                          {summary.reduction > 0 ? `− ${formatPrice(summary.reduction)}` : "—"}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeDiscountRow(row._id)}
                            aria-label="Remove discount"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes — collapsible */}
      <Card>
        <CardHeader className="py-0">
          <button
            type="button"
            className="flex w-full items-center gap-2 py-4 text-left"
            aria-expanded={notesOpen}
            onClick={() => setNotesOpen((o) => !o)}
          >
            {notesOpen ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <CardTitle>Notes</CardTitle>
            {!notesOpen && header.notes.trim() && (
              <Badge variant="neutral" className="ml-1">
                Has notes
              </Badge>
            )}
          </button>
        </CardHeader>
        {notesOpen && (
          <CardContent>
            <MarkdownNotes
              key={currentProposalId ?? "new"}
              value={header.notes}
              onChange={(v) => setHeader((h) => ({ ...h, notes: v }))}
              placeholder="How was this proposal constructed — assumptions, approvals, special terms… (markdown + tables supported)"
              minHeightPx={120}
              initialMode={currentProposalId ? "preview" : "edit"}
            />
          </CardContent>
        )}
      </Card>

      {/* Summary */}
      {validLines.length > 0 && (
        <>
          <div
            className={cn(
              "grid grid-cols-1 gap-4 md:grid-cols-2",
              header.locations && header.locations > 0 ? "lg:grid-cols-4" : "lg:grid-cols-3",
            )}
          >
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Monthly Recurring Revenue</p>
                <p className="mt-1 text-3xl font-semibold">{formatPrice(totals.mrr)}</p>
                {totals.totalReduction > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Gross {formatPrice(totals.grossMrr)} − {formatPrice(totals.totalReduction)} discounts
                  </p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  Annual Recurring Revenue ({header.contract_months || 12} mo)
                </p>
                <p className="mt-1 text-3xl font-semibold">{formatPrice(totals.arr)}</p>
                <div className="mt-3 border-t pt-3">
                  <p className="text-xs text-muted-foreground">
                    1st 12-month value{projection.hasStub ? " (incl. P1)" : ""}
                  </p>
                  <p className="text-lg font-semibold">{formatPrice2(projection.firstYearTotal)}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Blended Gross Margin</p>
                <div className="mt-1 flex items-center gap-2">
                  <p className="text-3xl font-semibold">{totals.blendedMargin.toFixed(1)}%</p>
                  <Badge
                    variant={marginVariant(totals.blendedMargin)}
                    role="button"
                    tabIndex={0}
                    title="View margin thresholds"
                    aria-label={`Margin status: ${marginLabel(totals.blendedMargin)}. View thresholds.`}
                    className="cursor-pointer hover:opacity-80"
                    onClick={() => setMarginInfoOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setMarginInfoOpen(true);
                      }
                    }}
                  >
                    {marginLabel(totals.blendedMargin)}
                  </Badge>
                </div>
                {marginNote(totals.blendedMargin) && (
                  <p className="mt-1 text-xs leading-snug text-muted-foreground">
                    {marginNote(totals.blendedMargin)}
                  </p>
                )}
              </CardContent>
            </Card>
            {header.locations && header.locations > 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">
                    Per Location ({header.locations.toLocaleString()})
                  </p>
                  <p className="mt-1 text-3xl font-semibold">
                    {formatPrice(totals.mrr / header.locations)}
                    <span className="text-base font-normal text-muted-foreground">/mo</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatPrice(totals.arr / header.locations)}/yr per location
                  </p>
                </CardContent>
              </Card>
            ) : null}
          </div>

          {attention && (
            <Alert variant={totals.blendedMargin < 50 ? "danger" : "warning"}>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{marginAlertTitle(totals.blendedMargin)}</AlertTitle>
              <AlertDescription>
                {marginNote(totals.blendedMargin)}{" "}
                {(() => {
                  const lowLines = validLines.filter((li) => priceOf(li) > 0 && lineMargin(idx, li) < 50);
                  if (!lowLines.length) return null;
                  return (
                    <>
                      Lines below 50%: {lowLines.map((li) => productName(idx, li)).join(", ")}.
                    </>
                  );
                })()}
              </AlertDescription>
            </Alert>
          )}

          <ProposalTimelineChart
            idx={idx}
            lines={lineItems}
            discountRows={discountRows}
            renewal={renewal}
            bundles={bundles}
          />
        </>
      )}
        </TabsContent>

        <TabsContent value="saved">
          {proposals.length === 0 ? (
            <Card>
              <CardContent>
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No saved proposals yet. Build a proposal on the Calculator tab and click Save
                  Proposal.
                </p>
              </CardContent>
            </Card>
          ) : (
            <SavedProposalsTable
              proposals={proposals}
              closers={closers}
              idx={idx}
              onLoad={loadProposal}
              onDelete={canWrite ? handleDelete : undefined}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Blood penalty for saving an "Absolutely Not" margin */}
      <BloodDripOverlay
        active={bleeding}
        onComplete={() => {
          setBleeding(false);
          const run = pendingSaveRef.current;
          pendingSaveRef.current = null;
          run?.();
        }}
      />

      {/* Save dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Proposal</DialogTitle>
            <DialogDescription>Give this proposal a name to save it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="save-name">Proposal Name *</Label>
              <Input
                id="save-name"
                placeholder="Q3 2026 Proposal"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="save-customer">Customer Name</Label>
              <Input
                id="save-customer"
                placeholder="Acme Corp"
                value={saveCustomer}
                onChange={(e) => setSaveCustomer(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => withBloodPenalty(() => void handleSave())}
              disabled={!saveName.trim() || createM.isPending}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share link met an unsaved draft — the user picks which one wins.
          Dismissing counts as "review my draft": nothing is lost by default. */}
      <Dialog
        open={linkConflict != null}
        onOpenChange={(open) => {
          if (!open) reviewDraftInstead();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>You have an unsaved draft</DialogTitle>
            <DialogDescription>
              This link opens a saved proposal, but there is an unsaved draft in this browser
              {(() => {
                const label =
                  linkConflict?.draft.header.proposal_name ||
                  linkConflict?.draft.header.customer_name;
                return label ? (
                  <>
                    {" "}
                    (<strong>{label}</strong>)
                  </>
                ) : null;
              })()}
              . Loading the shared proposal will discard that draft.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={reviewDraftInstead}>
              Review my draft
            </Button>
            <Button onClick={loadSharedInstead}>Load shared proposal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace confirm */}
      <Dialog open={replaceOpen} onOpenChange={setReplaceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace proposal?</DialogTitle>
            <DialogDescription>
              Overwrite <strong>{editingName ?? "this proposal"}</strong> with the current changes? This
              can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => withBloodPenalty(() => void handleReplace())}
              disabled={updateM.isPending}
            >
              Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Margin rubric reference */}
      <Dialog open={marginInfoOpen} onOpenChange={setMarginInfoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Blended gross margin thresholds</DialogTitle>
            <DialogDescription>
              How the blended margin maps to a status and its commission/approval impact.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {marginRubric.map((band) => {
              const active = band.label === marginLabel(totals.blendedMargin);
              return (
                <div
                  key={band.label}
                  className={cn(
                    "rounded-md border p-3",
                    active ? "border-power bg-power-light" : "border-border",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant={band.variant}>{band.label}</Badge>
                    <span className="text-sm font-medium text-muted-foreground">{band.range}</span>
                  </div>
                  <p className="mt-2 text-sm leading-snug text-muted-foreground">
                    {band.description}
                  </p>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={() => setMarginInfoOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer-facing preview */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Customer-facing Preview</DialogTitle>
            <DialogDescription>
              What the prospect sees on the quote — packages appear as one line, and “Unlimited”
              items hide the internal quantity estimate.
            </DialogDescription>
          </DialogHeader>
          <CustomerPreview idx={idx} lines={lineItems} bundles={bundles} mrr={totals.mrr} />
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
